import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useHelpSearch } from "@/hooks/useHelpSearch";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Search, ChevronRight, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";

// Highlight matching text
function HighlightedText({ text, query }: { text: string; query: string }) {
  if (!query || !text) return <>{text}</>;
  
  const parts = text.split(new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));
  
  return (
    <>
      {parts.map((part, i) => 
        part.toLowerCase() === query.toLowerCase() ? (
          <mark key={i} className="bg-primary/20 text-foreground px-0.5 rounded">
            {part}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </>
  );
}

interface HelpSearchDropdownProps {
  onSearchChange?: (query: string) => void;
  className?: string;
}

export function HelpSearchDropdown({ onSearchChange, className }: HelpSearchDropdownProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  
  const { data: results, isLoading } = useHelpSearch(query);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  
  const handleInputChange = (value: string) => {
    setQuery(value);
    setIsOpen(value.length >= 2);
    onSearchChange?.(value);
  };
  
  const handleResultClick = (articleId: string) => {
    setIsOpen(false);
    setQuery("");
    navigate(`/help/article/${articleId}`);
  };
  
  const handleViewAllClick = () => {
    setIsOpen(false);
    navigate(`/help/search?q=${encodeURIComponent(query)}`);
  };
  
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && query.length >= 2) {
      handleViewAllClick();
    }
    if (e.key === "Escape") {
      setIsOpen(false);
    }
  };
  
  const showDropdown = isOpen && query.length >= 2;
  const displayResults = results?.slice(0, 5) || [];

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      {/* Search Input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          ref={inputRef}
          placeholder="Search help articles..."
          value={query}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={() => query.length >= 2 && setIsOpen(true)}
          onKeyDown={handleKeyDown}
          className="pl-10"
        />
      </div>
      
      {/* Dropdown */}
      {showDropdown && (
        <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-popover border rounded-lg shadow-lg overflow-hidden">
          {/* Loading State */}
          {isLoading && (
            <div className="p-3 space-y-2">
              {[1, 2, 3].map(i => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          )}
          
          {/* Results */}
          {!isLoading && displayResults.length > 0 && (
            <>
              <div className="max-h-80 overflow-y-auto">
                {displayResults.map((result, index) => (
                  <button
                    key={result.article.id}
                    onClick={() => handleResultClick(result.article.id)}
                    className={cn(
                      "w-full text-left px-4 py-3 hover:bg-muted/50 transition-colors flex items-start gap-3",
                      index > 0 && "border-t"
                    )}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">
                        <HighlightedText text={result.article.title} query={query} />
                      </p>
                      {result.snippet && (
                        <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">
                          <HighlightedText text={result.snippet} query={query} />
                        </p>
                      )}
                      <Badge variant="outline" className="mt-1.5 text-xs">
                        {result.article.category?.name}
                      </Badge>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-1" />
                  </button>
                ))}
              </div>
              
              {/* View All Link */}
              {results && results.length > 5 && (
                <button
                  onClick={handleViewAllClick}
                  className="w-full text-left px-4 py-3 bg-muted/30 hover:bg-muted/50 transition-colors border-t flex items-center justify-between text-sm font-medium text-primary"
                >
                  <span>View all {results.length} results</span>
                  <ArrowRight className="h-4 w-4" />
                </button>
              )}
            </>
          )}
          
          {/* No Results */}
          {!isLoading && displayResults.length === 0 && (
            <div className="p-4 text-center">
              <p className="text-sm text-muted-foreground">No articles found for "{query}"</p>
              <button
                onClick={handleViewAllClick}
                className="text-sm text-primary hover:underline mt-2"
              >
                Search all articles
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
